import React from "react";
import { cn } from "@/utils/utils";

type Status = "no-setup" | "configured" | "not-configured";

const STATUS_CONFIG: Record<Status, { label: string; className: string }> = {
  "no-setup": {
    label: "NO SETUP NEEDED",
    className:
      "border-gray-300 text-gray-500 dark:border-slate-600 dark:text-gray-400",
  },
  configured: {
    label: "CONFIGURED \u2713",
    className:
      "border-green-500 text-green-600 dark:border-green-500 dark:text-green-400",
  },
  "not-configured": {
    label: "NOT CONFIGURED",
    className:
      "border-amber-500 text-amber-600 dark:border-amber-500 dark:text-amber-400",
  },
};

interface IntegrationCardProps {
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  isActive: boolean;
  isSelected: boolean;
  label: string;
  onClick: () => void;
  onSwitch: () => void;
  status: Status;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  description,
  icon: Icon,
  isActive,
  isSelected,
  label,
  onClick,
  onSwitch,
  status,
}) => {
  const { label: statusLabel, className: statusClassName } =
    STATUS_CONFIG[status];

  return (
    <div
      className={cn(
        "relative rounded-lg border p-4 transition-colors cursor-pointer",
        isSelected
          ? "border-indigo-500 bg-indigo-50/50 dark:border-indigo-400 dark:bg-indigo-950/30"
          : "border-gray-200 bg-white hover:border-gray-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600",
      )}
      onClick={onClick}
    >
      {isActive && (
        <span className="absolute top-3 right-3 text-[10px] font-medium px-1.5 py-px rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          ACTIVE
        </span>
      )}
      <div className="flex items-start gap-3">
        <Icon
          className={cn(
            "h-5 w-5 mt-0.5 flex-shrink-0",
            isSelected
              ? "text-indigo-600 dark:text-indigo-400"
              : "text-gray-400 dark:text-gray-500",
          )}
        />
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {label}
          </span>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            {description}
          </p>
          <div className="flex items-center justify-between mt-2">
            <span
              className={cn(
                "text-[10px] font-medium px-1.5 py-px rounded border",
                statusClassName,
              )}
            >
              {statusLabel}
            </span>
            {!isActive && (
              <button
                className="text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  onSwitch();
                }}
                type="button"
              >
                Switch &rarr;
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationCard;
